import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Elegant fullscreen preview modal for attachments (image or PDF).
 * Non-previewable types should not open this — the caller should trigger download directly.
 */
export default function AttachmentPreview({ attachment, open, onOpenChange }) {
    if (!attachment) return null;
    const isImg = attachment.mime?.startsWith("image/");
    const isPdf = attachment.mime === "application/pdf";
    const url = `${BACKEND}${attachment.url}`;
    const downloadUrl = `${url}?download=1`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                dir="rtl"
                data-testid="attachment-preview-modal"
                className="max-w-6xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col bg-[#0F172A] border-white/10 overflow-hidden"
            >
                <DialogHeader className="bg-[#0F172A] text-white px-5 py-3 border-b border-white/10 flex-row items-center justify-between gap-3 space-y-0">
                    <DialogTitle className="text-white text-sm font-semibold truncate flex-1 min-w-0">
                        {attachment.filename}
                    </DialogTitle>
                    <div className="flex items-center gap-2 shrink-0">
                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="attachment-preview-open-new-tab"
                            title="فتح في تبويب جديد"
                            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                        </a>
                        <a
                            href={downloadUrl}
                            download={attachment.filename}
                            data-testid="attachment-preview-download"
                            title="تحميل"
                            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                        >
                            <Download className="w-4 h-4" />
                        </a>
                        <button
                            onClick={() => onOpenChange(false)}
                            data-testid="attachment-preview-close"
                            aria-label="إغلاق"
                            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </DialogHeader>
                <div className="flex-1 min-h-0 bg-slate-800 flex items-center justify-center overflow-auto">
                    {isImg && (
                        <img
                            src={url}
                            alt={attachment.filename}
                            data-testid="attachment-preview-image"
                            className="max-w-full max-h-full object-contain"
                        />
                    )}
                    {isPdf && (
                        <iframe
                            src={url}
                            title={attachment.filename}
                            data-testid="attachment-preview-pdf"
                            className="w-full h-full bg-white"
                        />
                    )}
                    {!isImg && !isPdf && (
                        <div className="text-white/70 text-center p-8">
                            <div className="text-sm mb-4">لا يمكن عرض هذا النوع من الملفات هنا</div>
                            <Button asChild className="bg-[#C8102E] hover:bg-[#9F1239]">
                                <a href={downloadUrl} download={attachment.filename}>
                                    <Download className="w-4 h-4 ml-1" />
                                    تحميل الملف
                                </a>
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
